import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, FileText, Shield, Scale, Database } from 'lucide-react';
import { useConsentDocuments, useRecordMultipleConsents } from '@/hooks/useConsent';

interface ConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onComplete: () => void;
  requiredTypes?: string[];
}

const CONSENT_ICONS: Record<string, React.ReactNode> = {
  hipaa_authorization: <Shield className="h-4 w-4" />,
  privacy_policy: <FileText className="h-4 w-4" />,
  terms_of_service: <Scale className="h-4 w-4" />,
  data_processing: <Database className="h-4 w-4" />,
};

export function ConsentDialog({
  open,
  onOpenChange,
  userId,
  onComplete,
  requiredTypes = ['hipaa_authorization', 'privacy_policy', 'terms_of_service'],
}: ConsentDialogProps) {
  const { t } = useTranslation('common');
  const { data: documents, isLoading } = useConsentDocuments();
  const recordConsentsMutation = useRecordMultipleConsents();

  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);

  // Filter documents to required types
  const requiredDocs = documents?.filter(doc => 
    requiredTypes.includes(doc.consent_type)
  ) || [];

  // Initialize consents state when documents load
  useEffect(() => {
    if (requiredDocs.length > 0) {
      const initial: Record<string, boolean> = {};
      requiredDocs.forEach(doc => {
        initial[doc.id] = false;
      });
      setConsents(initial);
    }
  }, [documents]);

  const allConsented = requiredDocs.every(doc => consents[doc.id]);

  const handleConsentChange = (docId: string, checked: boolean) => {
    setConsents(prev => ({ ...prev, [docId]: checked }));
  };

  const handleSubmit = async () => {
    const consentData = requiredDocs.map(doc => ({
      document: doc,
      consented: consents[doc.id] || false,
    }));

    try {
      await recordConsentsMutation.mutateAsync({
        userId,
        consents: consentData,
      });
      onComplete();
      onOpenChange(false);
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const getConsentTitle = (type: string): string => {
    const titles: Record<string, string> = {
      hipaa_authorization: t('consent.types.hipaa', 'HIPAA Authorization'),
      privacy_policy: t('consent.types.privacy', 'Privacy Policy'),
      terms_of_service: t('consent.types.terms', 'Terms of Service'),
      data_processing: t('consent.types.dataProcessing', 'Data Processing Agreement'),
    };
    return titles[type] || type;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {t('consent.title', 'Review & Accept Agreements')}
          </DialogTitle>
          <DialogDescription>
            {t('consent.description', 'Please review and accept the following agreements to continue using our services.')}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea className="flex-1 max-h-[50vh] pr-4">
            <Accordion 
              type="single" 
              collapsible 
              value={expandedDoc || undefined}
              onValueChange={(value) => setExpandedDoc(value)}
              className="space-y-2"
            >
              {requiredDocs.map((doc) => (
                <AccordionItem 
                  key={doc.id} 
                  value={doc.id}
                  className="border rounded-lg px-4"
                >
                  <div className="flex items-center gap-3 py-4">
                    <Checkbox
                      id={`consent-${doc.id}`}
                      checked={consents[doc.id] || false}
                      onCheckedChange={(checked) => handleConsentChange(doc.id, !!checked)}
                    />
                    <div className="flex-1 flex items-center gap-2">
                      {CONSENT_ICONS[doc.consent_type]}
                      <Label 
                        htmlFor={`consent-${doc.id}`}
                        className="font-medium cursor-pointer"
                      >
                        {doc.title || getConsentTitle(doc.consent_type)}
                      </Label>
                    </div>
                    <AccordionTrigger className="py-0 hover:no-underline">
                      <span className="text-sm text-primary">
                        {t('consent.readFull', 'Read Full Text')}
                      </span>
                    </AccordionTrigger>
                  </div>
                  
                  <AccordionContent className="pb-4">
                    {doc.summary && (
                      <p className="text-sm text-muted-foreground mb-4 p-3 bg-muted rounded-md">
                        {doc.summary}
                      </p>
                    )}
                    <div className="text-sm whitespace-pre-wrap max-h-64 overflow-y-auto p-3 bg-muted/50 rounded-md">
                      {doc.content}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('consent.version', 'Version {{version}} - Effective {{date}}', {
                        version: doc.version,
                        date: new Date(doc.effective_date).toLocaleDateString(),
                      })}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </ScrollArea>
        )}

        <DialogFooter className="flex-shrink-0 mt-4">
          <p className="text-xs text-muted-foreground flex-1">
            {t('consent.required', 'You must accept all agreements to continue.')}
          </p>
          <Button
            onClick={handleSubmit}
            disabled={!allConsented || recordConsentsMutation.isPending}
          >
            {recordConsentsMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t('consent.acceptAndContinue', 'Accept & Continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
